// Career miniapp tracking hook — FB Pixel + Google Analytics + GTM dataLayer + TikTok Pixel
// Egyetlen hook, ami egyszerre küld a trackereknek event-et a route alapján.
// [F-TIKTOKPIXEL] TikTok: a page-view gazdája a betöltő (SSR career.tpl vagy utils/consent.ts),
// az SPA route-váltásokat a TikTok SDK automatikus SPA-mérése követi — a hook route-ra NEM küld
// ttq.page()-et, csak modul-eseményt (job -> ViewContent) és diszkrét eseményt (Search, SubmitForm).
// Pipeline-építhető FB Analytics-ban: PageView → ViewContent → InitiateCheckout → Lead → CompleteRegistration.
import { useEffect, useRef, useState } from 'react';
import { subscribeConsentChange } from '../utils/consent';
const EMPTY_TRACKING = { fb_pixel_id: '', ga_id: '', gtm_id: '', tiktok_pixel_id: '' };
function getTrackingConfig() {
    if (typeof window === 'undefined')
        return { ...EMPTY_TRACKING };
    const cfg = window.__CAREER_CONFIG__;
    if (!cfg || !cfg.tracking)
        return { ...EMPTY_TRACKING };
    const t = cfg.tracking;
    return {
        fb_pixel_id: t.fb_pixel_id || '',
        ga_id: t.ga_id || '',
        gtm_id: t.gtm_id || '',
        tiktok_pixel_id: t.tiktok_pixel_id || '',
    };
}
// [F-TIKTOKPIXEL] TikTok esemény — a Meta fbqTrack-kel azonos consent-guard: banner-módban
// targeting-hozzájárulás nélkül NULLA ttq hívás (visszavonás után a window.ttq még él).
// Legacy oldalon (nincs __CAREER_CONSENT__) a viselkedés: ha a stub él, megy az esemény.
export function ttqTrack(event, params) {
    if (typeof window === 'undefined')
        return;
    const ttq = window.ttq;
    if (!ttq || typeof ttq.track !== 'function')
        return;
    const consent = window.__CAREER_CONSENT__;
    if (consent && consent.t !== 1)
        return;
    try {
        if (params && Object.keys(params).length > 0) {
            ttq.track(event, params);
        }
        else {
            ttq.track(event);
        }
    }
    catch (e) {
        // silent — a tracking soha ne dobjon hibát az UI-nak
    }
}
// [F-TIKTOKPIXEL] mehet-e most TikTok esemény: legacy oldalon (nincs __CAREER_CONSENT__) igen,
// banner-módban kizárólag targeting-elfogadásnál.
function tiktokConsentAllows() {
    if (typeof window === 'undefined')
        return false;
    const consent = window.__CAREER_CONSENT__;
    return !consent || consent.t === 1;
}
// FB esemény-név -> TikTok standard esemény (csak ahol a szemantika eltér)
const TIKTOK_EVENT_MAP = {
    Lead: 'SubmitForm',
};
// Modul -> TikTok esemény: kizárólag a job-nézet küld ViewContent-et (a többi route csak
// pageview, amit az SDK mér). A thankyou-mount CompleteRegistration-t szándékosan NEM
// vesszük át (frissítés/közvetlen megnyitás dupla konverziót jelentene); a konverzió az
// onApplySuccess -> trackEvent('Lead') -> SubmitForm.
function buildTiktokEvents(p) {
    if (p.module !== 'job')
        return [];
    return [
        {
            name: 'ViewContent',
            // TikTok standard paraméter-séma: contents[] (content_id/content_type/content_name) + content_type
            params: {
                contents: [
                    {
                        content_id: String(p.jobId || ''),
                        content_type: 'product',
                        content_name: p.jobTitle || `Job ${p.jobId || ''}`,
                    },
                ],
                content_type: 'product',
                description: p.jobCategory || 'Job listing',
            },
        },
    ];
}
export function fbqTrack(event, params) {
    if (typeof window === 'undefined' || typeof window.fbq !== 'function')
        return;
    // [F-COOKIECMP] Banner-módban (a bootstrap létrehozza a __CAREER_CONSENT__-et)
    // targeting-hozzájárulás nélkül NULLA fbq hívás mehet ki — visszavonás után a
    // window.fbq még él, ezért a typeof-guard önmagában nem elég. Legacy oldalon
    // (nincs __CAREER_CONSENT__) a viselkedés változatlan.
    const consent = window.__CAREER_CONSENT__;
    if (consent && consent.t !== 1)
        return;
    try {
        if (params && Object.keys(params).length > 0) {
            window.fbq('track', event, params);
        }
        else {
            window.fbq('track', event);
        }
    }
    catch (e) {
        // silent fail — a tracking soha ne dobjon hibát az UI-nak
    }
}
export function gtagEvent(event, params) {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function')
        return;
    try {
        window.gtag('event', event, params || {});
    }
    catch (e) {
        // silent
    }
}
export function dataLayerPush(payload) {
    if (typeof window === 'undefined')
        return;
    if (!window.dataLayer || !Array.isArray(window.dataLayer)) {
        window.dataLayer = [];
    }
    try {
        window.dataLayer.push(payload);
    }
    catch (e) {
        // silent
    }
}
// Modul → FB Pixel event mapping
function buildFbEvents(p) {
    const events = [];
    switch (p.module) {
        case 'index':
            events.push({
                name: 'ViewContent',
                params: {
                    content_category: 'Career index',
                    content_name: 'Career listing page',
                    content_type: 'product_group',
                },
            });
            break;
        case 'job':
            events.push({
                name: 'ViewContent',
                params: {
                    content_category: p.jobCategory || 'Job listing',
                    content_name: p.jobTitle || `Job ${p.jobId || ''}`,
                    content_ids: p.jobId ? [String(p.jobId)] : [],
                    content_type: 'product',
                },
            });
            break;
        case 'cvupload':
            events.push({
                name: 'InitiateCheckout',
                params: {
                    content_category: 'CV upload',
                    content_name: 'CV form opened',
                },
            });
            break;
        case 'thankyou':
            events.push({
                name: 'CompleteRegistration',
                params: {
                    content_category: 'Thank you',
                    content_name: 'Application sent',
                    status: true,
                },
            });
            break;
        case 'content':
            events.push({
                name: 'ViewContent',
                params: {
                    content_category: 'Static page',
                    content_name: p.pageTitle || p.pageKey || 'Static content',
                },
            });
            break;
        case 'notfound':
            // 404 — csak PageView, nincs egyéb event
            break;
    }
    return events;
}
/**
 * Route-szintű tracking hook.
 * Mount-kor egyszer fut, és a 3 tracker mindegyikére küld event-et.
 *
 * Használat egy view komponens elején:
 *   useTracking({ module: 'index' });
 *   useTracking({ module: 'job', jobId: 42, jobTitle: 'Embedded C', jobCategory: 'IT' });
 */
export function useTracking(params) {
    const sentRef = useRef('');
    // [F-TIKTOKPIXEL] külön TikTok küldési állapot: (1) a modul-esemény csak a betöltött
    // állásadattal (jobTitle) megy ki, (2) banner-módban hozzájárulás nélkül NEM foglalja le a
    // kulcsot, és a consent-döntés mentésére (career:consent-changed) újra próbálkozik — így az
    // állásoldalon adott elfogadás után az aktuális állás ViewContent-je egyszer kimegy.
    const tiktokSentRef = useRef('');
    const [consentTick, setConsentTick] = useState(0);
    useEffect(() => subscribeConsentChange(() => setConsentTick((n) => n + 1)), []);
    useEffect(() => {
        const cfg = getTrackingConfig();
        if (!cfg.tiktok_pixel_id)
            return;
        const ttEvents = buildTiktokEvents(params);
        if (ttEvents.length === 0)
            return;
        if (params.module === 'job' && !params.jobTitle)
            return; // az állásadat még nem érkezett meg
        if (!tiktokConsentAllows())
            return; // consent-mentéskor (consentTick) újra fut
        const key = params.module + ':' + String(params.jobId || '') + ':' + String(params.pageKey || '');
        if (tiktokSentRef.current === key)
            return;
        tiktokSentRef.current = key;
        for (const ev of ttEvents) {
            ttqTrack(ev.name, ev.params);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.module, params.jobId, params.jobTitle, params.pageKey, consentTick]);
    useEffect(() => {
        const cfg = getTrackingConfig();
        // Logged-in user vagy admin nem konfigolt: skip
        if (!cfg.fb_pixel_id && !cfg.ga_id && !cfg.gtm_id && !cfg.tiktok_pixel_id)
            return;
        // Dedup: ugyanazt a kulcsot nem küldjük 2x ha re-render
        const key = JSON.stringify(params);
        if (sentRef.current === key)
            return;
        sentRef.current = key;
        // FB Pixel — PageView (mindig) + modulos events
        if (cfg.fb_pixel_id) {
            fbqTrack('PageView');
            const fbEvents = buildFbEvents(params);
            for (const ev of fbEvents) {
                fbqTrack(ev.name, ev.params);
            }
        }
        // GA gtag — page_view event SPA navigation-re
        if (cfg.ga_id && typeof window !== 'undefined') {
            gtagEvent('page_view', {
                page_path: window.location.pathname + window.location.search,
                page_title: params.pageTitle || document.title,
                page_location: window.location.href,
            });
        }
        // GTM dataLayer push — career-specific custom event
        if (cfg.gtm_id) {
            dataLayerPush({
                event: 'careerPageView',
                career_module: params.module,
                career_job_id: params.jobId || null,
                career_job_title: params.jobTitle || null,
                career_page_key: params.pageKey || null,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.module, params.jobId, params.pageKey]);
}
/**
 * Diszkrét event helper — pl. SearchBar Search vagy ApplyForm Lead.
 * Minden konfigurált tracker számára küld.
 * [F-TIKTOKPIXEL] tiktokEventName: a TikTok standard esemény neve; ha nincs megadva,
 * a TIKTOK_EVENT_MAP (Lead -> SubmitForm) vagy az FB-név megy. tiktokParams: ha a TikTok
 * paraméter-séma eltér (pl. Search: search_string), különben a közös params.
 */
export function trackEvent(fbEventName, params = {}, gaEventName, dataLayerEvent, tiktokEventName, tiktokParams) {
    const cfg = getTrackingConfig();
    if (cfg.fb_pixel_id)
        fbqTrack(fbEventName, params);
    if (cfg.ga_id)
        gtagEvent(gaEventName || fbEventName.toLowerCase(), params);
    if (cfg.gtm_id) {
        dataLayerPush({ event: dataLayerEvent || `career${fbEventName}`, ...params });
    }
    if (cfg.tiktok_pixel_id) {
        ttqTrack(tiktokEventName || TIKTOK_EVENT_MAP[fbEventName] || fbEventName, tiktokParams || params);
    }
}
